import { ApplicationName, ApplicationPaths } from "./ApiAuthorizationConstants";
import { UserManager, WebStorageStateStore } from "oidc-client";
import axios from 'axios';
import HelperFunctions from 'utils/HelperFunctions';

export class AuthorizeService {
  _callbacks = [];
  _nextSubscriptionId = 0;
  _user = null;
  _isAuthenticated = false;

  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.
  _popUpDisabled = true;

  async isAuthenticated() {
    const user = await this.getUser();
    return !!user;
  }

  async getUser() {
    if (this._user && this._user.profile) {
      return this._user.profile;
    }

    await this.ensureUserManagerInitialized();
    const user = await this.userManager.getUser();
    const token = user && user.access_token;
    //console.log('in AuthorizeService.getUser', token);
    HelperFunctions.setCookie("Authorization", token, 1);
    return user && user.profile;
  }

  async getAccessToken() {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager.getUser();
    const token = user && user.access_token;

    //console.log('in AuthorizeService.getAccessToken', token);

    HelperFunctions.setCookie("Authorization", token, 1);

    return token;
  }

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  async signIn(state) {
    //console.log('In AuthorizeService.signIn Start', state);
    await this.ensureUserManagerInitialized();
    try {
      const silentUser = await this.userManager.signinSilent(
        this.createArguments()
      );
      this.updateState(silentUser);
      return this.success(state);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      //console.log("Silent authentication error: ", silentError);

      try {
        if (this._popUpDisabled) {
          throw new Error(
            "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
          );
        }

        const popUpUser = await this.userManager.signinPopup(
          this.createArguments()
        );
        this.updateState(popUpUser);
        return this.success(state);
      } catch (popUpError) {
        if (popUpError.message === "Popup window closed") {
          // The user explicitly cancelled the login action by closing an opened popup.
          return this.error("The user closed the window.");
        } else if (!this._popUpDisabled) {
          console.log("Popup authentication error: ", popUpError);
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await this.userManager.signinRedirect(this.createArguments(state));
          return this.redirect();
        } catch (redirectError) {
          console.log("Redirect authentication error: ", redirectError);
          return this.error(redirectError);
        }
      }
    }
    //console.log('In AuthorizeService.signIn End', state);
  }

  async completeSignIn(url) {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager.signinCallback(url);
      if (!user) {
        console.log("Signin callback returned null user, redirect the user");
        await this.userManager.signinRedirect();
        return this.redirect();
      }
      this.updateState(user);

      // check redirection to partitionKey
      const partitionKey = localStorage.getItem('pendingPartitionKey');
      if(partitionKey) {
        axios.get(`${import.meta.env.VITE_VNEXT_BASE_URL}` + `api/ESP/Bee/GetDesignIdByPartitionKey/${partitionKey}`)
        .then(resp => {
          const brandId = localStorage.getItem('brandId');
          const designId = resp.data.content;
          localStorage.removeItem('pendingPartitionKey');

          window.location.href =
            `/EmailDesigner/BeePage/${designId}/${brandId}`;
          });
      }
      else {
        return this.success(user && user.state);
      }

    } catch (error) {
      if (error.message && error.message.indexOf("No matching state") !== -1) {
        console.log("No matching state found in storage.");
        const storedUser = await this.userManager.getUser();
        if (storedUser) {
          this.updateState(storedUser);
          return this.success(storedUser && storedUser.state);
        } else {
          console.log("User not found in storage, redirect to signin page.");
          await this.userManager.signinRedirect();
          return this.redirect();
        }
      }
      console.log("There was an error signing in: ", error);
      return this.error("There was an error signing in.");
    }
  }
  

  // We try to sign out the user in two different ways:
  // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
  //    post logout redirect flow.
  async signOut(state) {
    //console.log('In AuthorizeService.signout Start ', state);
    await this.ensureUserManagerInitialized();
    try {
      this.updateState(null);
      await this.userManager.signoutRedirect(this.createArguments(state));
      //console.log('In AuthorizeService.signout end ', state);
      return this.redirect();
    } catch (redirectSignOutError) {
      console.log("Redirect signout error: ", redirectSignOutError);
      return this.error(redirectSignOutError);
    }
  }

  async completeSignOut(url) {
    //console.log('In AuthorizeService.completeSignouyt End ', url);
    await this.ensureUserManagerInitialized();
    try {
      const response = await this.userManager.signoutCallback(url);
      this.updateState(null);
      //console.log('In AuthorizeService.completeSignout End ', url, response);
      return this.success(response && response.data);
    } catch (error) {
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error(error);
    }
  }

  updateState(user) {
    //console.log('In AuthorizeService.updateState start ', user);
    if (user && user.profile && user.profile.sub) {
      localStorage.setItem("user.guid", user.profile.sub);
      localStorage.setItem("user.firstName", user.profile.FirstName);
      localStorage.setItem("user.lastName", user.profile.LastName);
      localStorage.setItem("user.email", user.profile.Email);
      localStorage.setItem("user.userName", user.profile.UserName);
      localStorage.setItem("user.sessionTimeoutInMin", user.profile.SessionTimeoutInMin);
      localStorage.setItem("audienceSelector.advanceQueries", "false");
    } else {
      localStorage.setItem("user.guid", undefined);
      localStorage.setItem("user.firstName", undefined);
      localStorage.setItem("user.lastName", undefined);
      localStorage.setItem("user.email", undefined);
      localStorage.setItem("user.userName", undefined);
      localStorage.setItem("user.lastLoginDate", undefined);
      localStorage.setItem("user.sessionTimeoutInMin", undefined);
      localStorage.setItem("popupOpen", undefined);
      // localStorage.setItem("popupLocked", undefined);
      localStorage.setItem("popupCountdown", undefined);
    }
    this._user = user;
    this._isAuthenticated = !!this._user;
    this.notifySubscribers();
    //console.log('In AuthorizeService.updateState End ', user);
  }

  subscribe(callback) {
    this._callbacks.push({
      callback,
      subscription: this._nextSubscriptionId++
    });
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId) {
    const subscriptionIndex = this._callbacks
      .map((element, index) =>
        element.subscription === subscriptionId
          ? { found: true, index }
          : { found: false }
      )
      .filter((element) => element.found === true);
    if (subscriptionIndex.length !== 1) {
      throw new Error(
        `Found an invalid number of subscriptions ${subscriptionIndex.length}`
      );
    }

    this._callbacks.splice(subscriptionIndex[0].index, 1);
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback();
    }
  }

  createArguments(state) {
    return { useReplaceToNavigate: true, data: state };
  }

  error(message) {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  success(state) {
    return { status: AuthenticationResultStatus.Success, state };
  }

  redirect() {
    return { status: AuthenticationResultStatus.Redirect };
  }

  async ensureUserManagerInitialized() {
    if (this.userManager !== undefined) {
      return;
    }


    let response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl);

    if (!response.ok) {
      throw new Error(`Could not load settings for '${ApplicationName}'`);
    }

    let settings = await response.json();
    settings.automaticSilentRenew = true;
    settings.includeIdTokenInSilentRenew = true;
    // Disable session monitor iframe — it polls uatauth.ascent360.com cross-origin which
    // Safari ITP blocks, producing "Blocked a frame" console errors. Silent renew still works.
    settings.monitorSession = false;
    settings.userStore = new WebStorageStateStore({
      prefix: ApplicationName
    });

    this.userManager = new UserManager(settings);

    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager.removeUser();
      this.updateState(undefined);

    });

    this.userManager.events.addUserLoaded(async () => {

      var token = await this.getAccessToken();
      
      return token;
    });

  }

  static get instance() {
    return authService;
  }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
  Redirect: "redirect",
  Success: "success",
  Fail: "fail"
};
